import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material";
import React, { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { useParams, useSearchParams } from "react-router-dom";
import { FileDownloadOutlined as DownloadTasks } from "@mui/icons-material";

import manageColumnIcon from "../../../../styles/svg/Vector.svg";
import { useWorkflowById } from "../../../../utils/reactQuery/workflows";
import { dataGridColumn } from "../../../../components/dataGridColumn";
import OpenCardDetail from "../Kanban/openCardDetail";
import addIcon from "../../../../styles/svg/addIcon.svg";
import CreateCardDialogListview from "./CreateCardDialogListview";
import { currentUserId, isAdmin } from "../../../../utils/constants";
import ChildTaskData from "./ChildTaskData";
import Loader from "../../../../components/Loader";
import { manageColumnOrder } from "../../../../sevices/apiCards";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { generateCSV, objectToFormData } from "../../../../utils";
import {
  setWorkflowIds,
  setMilestoneIds,
  setAssigneeIds,
} from "../../../../redux/ListViewColumnHeaderFilterSclice";
import { useResponsive } from "../../../../hooks/useResponsive";
import useToastPromise from "../../../../hooks/useToast";
import CustomMenu from "../../../../components/Menu";
import { CustomButton } from "../../../../components/Button/Button";
import "./ListView.scss";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import ColumnCheckboxes from "./ColumnCheckboxes";
import { generateCSVData } from "../../../../utils/csvUtils";
import useBoardIdByKey from "../../../../hooks/useBoardIdByKey";
const GlobalTableComponent = React.lazy(() =>
  import("../../../../components/Table/Table/Table")
);

export default function ListView({
  workflowId,
  boardName,
  cards,
  isLoading,
  allCardsByBoard,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [expandcardId, setExpandCardId] = useState(null);
  const [subtaskCards, setSubtaskCards] = useState([]);
  const [isSubtaskLoading, setIsSubtaskLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [boardCards, setBoardCards] = useState(cards);
  const { id } = useBoardIdByKey();
  const { data: workflows } = useWorkflowById(workflowId, id);
  const [openCreateCard, setOpenCreateCard] = useState(false);
  const filterUser = searchParams.get("search")?.split(",");
  const noMileStoneSearch = searchParams.get("noMilestone");
  const epicSearch = useSelector((state) => state.epic.searchEpic);
  const searchText = useSelector((state) => state.search.searchText);
  const cardId = searchParams.get("card_id");
  const statusSearch = searchParams.get("workflow_Step");
  const columnHeaderStatus = searchParams.get("statusFilter");
  const columnHeaderMilestone = searchParams.get("columnMilestone");
  const columnHeaderAssignee = searchParams.get("assigneeFilter");
  const milestoneIsSelected = searchParams.get("milestone");
  const currentUserIdVariable = currentUserId();
  const queryClient = useQueryClient();
  const isMobile = useResponsive("down", "md");
  const { showToastPromise } = useToastPromise();
  const [quickTasks, setQuickTasks] = useCustomSearchParams("quickTasks");

  const havePermissionToEdit = (data) => {
    if (
      isAdmin() ||
      boardName?.owner_user_id == currentUserIdVariable ||
      boardName?.board_permission?.is_admin
    ) {
      return true;
    } else {
      return false;
    }
  };
  const [columns, setColumns] = useState(
    dataGridColumn(boardName, havePermissionToEdit, boardName?.board_permission)
  );

  const reorderColumns = (dataGridColumns, columnSequence) => {
    if (!columnSequence) return dataGridColumns;
    return dataGridColumns.sort(
      (a, b) => columnSequence[a.id] - columnSequence[b.id]
    );
  };
  const reorderedColumns = reorderColumns(
    dataGridColumn(
      boardName,
      havePermissionToEdit,
      boardName?.board_permission
    ),
    boardName?.card_column_sequence
  );

  useEffect(() => {
    if (boardName?.card_column_sequence) {
      setColumns(reorderedColumns);
    }
  }, [boardName?.card_column_sequence]);

  const handleSelectColumn = (columnId) => {
    setColumns(
      columns.map((item) =>
        item.id === columnId ? { ...item, isHidden: !item.isHidden } : item
      )
    );
  };

  const workflowSteps = workflows?.data?.data?.responseData;

  useEffect(() => {
    let project;

    if (subtaskCards) {
      project = boardCards?.map((item, idx) => {
        if (item?.card?.card_id === expandcardId) {
          return { ...item, subRow: subtaskCards };
        }

        return item;
      });
      setBoardCards(project);
    }
  }, [subtaskCards]);

  useEffect(() => {
    const debounceDelay = 1000;
    let allFilteredCards = cards;
    const debouncedFilter = debounce(() => {
      if (searchText) {
        allFilteredCards = allFilteredCards?.filter(
          (cardData) =>
            (cardData?.card?.title
              ? `${cardData?.card?.title}`.toLowerCase().includes(searchText)
              : "") ||
            cardData?.card?.card_id
              ?.toString()
              ?.toLowerCase()
              ?.includes(searchText) ||
            (cardData?.card.assigned_to_user?.first_name
              ? `${cardData?.card.assigned_to_user?.first_name}`
                  .toLowerCase()
                  .includes(searchText)
              : "")
        );
        setBoardCards(allFilteredCards);
      }
    }, debounceDelay);
    if (filterUser) {
      searchParams.set("assigneeFilter", "");
      searchParams.delete("assigneeFilter");
      setSearchParams(searchParams);
      const numericFilterUser = filterUser.map(Number);
      allFilteredCards = allFilteredCards?.filter((el) =>
        numericFilterUser?.includes(el?.card?.assigned_to_user_id)
      );
    }
    if (epicSearch && epicSearch?.length > 0) {
      searchParams.set("columnMilestone", "");
      searchParams.delete("columnMilestone");
      setSearchParams(searchParams);
      allFilteredCards = allFilteredCards?.filter((cardData) =>
        epicSearch.includes(cardData?.card?.epic_id)
      );
    }

    if (noMileStoneSearch === "true") {
      allFilteredCards = allFilteredCards?.filter(
        (cardData) => cardData?.card?.epic_id == null
      );
    }

    if (searchText) {
      debouncedFilter();
    }

    if (statusSearch) {
      searchParams.set("statusFilter", "");
      searchParams.delete("statusFilter");
      setSearchParams(searchParams);
      allFilteredCards = allFilteredCards?.filter((cardData) => {
        return cardData?.card?.workflow_step_id == statusSearch;
      });
    }
    if (columnHeaderStatus) {
      const columnStatusArray = columnHeaderStatus?.split(",");
      allFilteredCards = allFilteredCards?.filter((cardData) =>
        columnStatusArray?.includes(
          cardData?.card?.workflow_step_id?.toString()
        )
      );
    }

    if (columnHeaderMilestone) {
      const columnMilestoneArray = columnHeaderMilestone?.split(",");
      allFilteredCards = allFilteredCards?.filter((cardData) =>
        columnMilestoneArray?.includes(
          cardData?.card?.epic?.epic_id?.toString()
        )
      );
    }

    if (columnHeaderAssignee) {
      const columnAssigneeArray = columnHeaderAssignee?.split(",");
      allFilteredCards = allFilteredCards?.filter((cardData) => {
        const userId = cardData?.card?.assigned_to_user?.user_id?.toString();

        return (
          columnAssigneeArray.includes(userId) ||
          (columnAssigneeArray.includes("unassigned") && !userId)
        );
      });
    }

    setBoardCards(allFilteredCards);

    return () => {
      debouncedFilter.cancel();
    };
  }, [
    searchText,
    filterUser,
    epicSearch,
    id,
    cards,
    statusSearch,
    noMileStoneSearch,
    columnHeaderStatus,
    columnHeaderMilestone,
    columnHeaderAssignee,
  ]);
  const handleClickOpen = () => {
    setOpenCreateCard(true);
  };

  const handleExpandedData = (cardId) => {
    setExpandCardId(cardId);
  };

  const handleSelectAll = () => {
    if (
      columns.every((value) =>
        value.id === "cardId" ||
        value.id === "title" ||
        value.id === "assigned_to_user_name"
          ? true
          : value.isHidden === true
      )
    ) {
      setColumns(
        columns.map((item) =>
          item.id === "cardId" ||
          item.id === "title" ||
          item.id === "assigned_to_user_name"
            ? { ...item }
            : { ...item, isHidden: false }
        )
      );
    } else {
      setColumns(
        columns.map((item) =>
          item.id === "cardId" ||
          item.id === "title" ||
          item.id === "assigned_to_user_name"
            ? { ...item }
            : { ...item, isHidden: true }
        )
      );
    }
  };

  const loader = {
    style: { color: "black" || "#ffffff" },
    size: 20,
  };

  const subTableProps = {
    isCollapsible: true,
    isHiddenRowExpandIcon: true,
    isHeaderHidden: true,
    isLoading: isSubtaskLoading,
    loadingProps: loader,
  };

  const { mutate } = useMutation({
    mutationFn: async (data) => {
      return await showToastPromise(manageColumnOrder, data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["boardName", id],
      });
    },
    onError: (error) => {
      console.error("column sequence not updated", error);
    },
  });

  const handleColumOrder = (data) => {
    const newArrangement = {};
    data?.map((item, index) => {
      newArrangement[item.id] = index;
    });

    mutate(
      objectToFormData({
        field_sequence: JSON.stringify(newArrangement),
      })
    );
  };

  useEffect(() => {
    const updateWorkflowIds = () => {
      let ids = [];
      const parsedStatusSearch = Number(statusSearch);
      if (parsedStatusSearch) {
        ids = [parsedStatusSearch];
      } else if (cards?.length > 0) {
        ids = [...new Set(cards?.map((item) => item?.card?.workflow_step_id))];
      }

      if (ids.length > 0) {
        dispatch(
          setWorkflowIds({
            stepIds: ids,
            workflowId: boardName?.workflow_id,
          })
        );
      }
    };

    updateWorkflowIds();
  }, [statusSearch, cards, dispatch, boardName?.workflow_id]);

  useEffect(() => {
    let ids = [];
    const parsedMilestoneSearch = Number(epicSearch);
    if (parsedMilestoneSearch) {
      if (cards?.length > 0) {
        ids = [
          ...new Map(
            cards
              .map((item) => item.card?.epic) // Extract the epic object
              .filter((epic) => epic?.epic_id === parsedMilestoneSearch) // Filter out epics with null epic_id
              .map((epic) => [epic.epic_id, epic]) // Map epic_id to the epic object to ensure uniqueness
          ).values(),
        ];
      }
    } else if (cards.length > 0) {
      ids = [
        ...new Map(
          cards
            .map((item) => item.card?.epic) // Extract the epic object
            .filter((epic) => epic?.epic_id != null) // Filter out epics with null epic_id
            .map((epic) => [epic.epic_id, epic]) // Map epic_id to the epic object to ensure uniqueness
        ).values(),
      ];
    }
    if (ids?.length > 0) {
      dispatch(setMilestoneIds(ids));
    }
  }, [cards, dispatch, epicSearch]);

  useEffect(() => {
    let ids = [];
    const parsedAssigneeSearch = Number(filterUser);
    if (parsedAssigneeSearch) {
      if (cards?.length > 0) {
        ids = [
          ...new Map(
            cards
              .map((item) => item?.card?.assigned_to_user) // Extract the epic object
              .filter((assignee) => assignee?.user_id === parsedAssigneeSearch)
              .map((assignee) => [assignee?.user_id, assignee]) // Map epic_id to the epic object to ensure uniqueness
          ).values(),
        ];
      }
    } else if (cards.length > 0) {
      ids = [
        ...new Map(
          cards
            .map((item) => item?.card?.assigned_to_user) // Extract the epic object
            .filter((assignee) => assignee !== null)
            .map((assignee) => [assignee?.user_id, assignee]) // Map epic_id to the epic object to ensure uniqueness
        ).values(),
      ];
    }
    if (ids?.length > 0) {
      dispatch(setAssigneeIds(ids));
    }
  }, [cards, dispatch, filterUser]);

  const handleGenerateCSVData = () => {
    generateCSVData(boardCards, allCardsByBoard, workflowSteps, columns);
  };

  const getRowClassName = (row) => {
    if (row.is_todo) {
      return "todo-row";
    }
    return "";
  };

  return (
    <>
      {expandcardId && (
        <ChildTaskData
          cardId={expandcardId}
          setSubtaskCards={setSubtaskCards}
          setIsSubtaskLoading={setIsSubtaskLoading}
        />
      )}
      {boardCards && cardId && <OpenCardDetail cardsOnBoard={boardCards} />}
      {openCreateCard && workflowSteps && (
        <CreateCardDialogListview
          open={openCreateCard}
          handleClose={() => setOpenCreateCard(false)}
          workflowSteps={workflowSteps}
        />
      )}
      <div>
        <Stack
          direction="row"
          className="d-flex justify-content-end align-items-center"
          mb={3}>
          <Stack
            direction="row"
            justifyContent="end"
            onClick={(event) => setAnchorEl(event.currentTarget)}>
            <img
              src={manageColumnIcon}
              width={14}
              alt="manage column"
              style={{ cursor: "pointer" }}
            />
            <Typography className="manage-board">Manage Columns</Typography>
          </Stack>
          <CustomButton
            className=" white-button capitalize ml-1"
            sx={{
              height: "38px",
              "&:hover": {
                outline: "none",
                backgroundColor: "rgb(240 249 254)",
                border: "1px solid #1b3e61",
              },
            }}
            variant="outlined">
            <label class="custom-checkbox">
              <input
                type="checkbox"
                checked={!quickTasks}
                onClick={() =>
                  setQuickTasks(
                    "quickTasks",
                    quickTasks === "false" ? "" : "false"
                  )
                }
              />
              <span class="checkmark"></span>
              Include quick tasks
            </label>
          </CustomButton>
          <CustomButton
            onClick={handleClickOpen}
            className="theme-bg-color capitalize ml-3 listview-create-card-button"
            variant="contained"
            startIcon={<img src={addIcon} alt="addSubtaskIcone" />}
            // disabled={!(havePermissionToEdit() || boardName?.board_permission?.can_create_card)}
          >
            Create Card
          </CustomButton>
          <CustomButton
            onClick={handleGenerateCSVData}
            className="secondary-background-color capitalize ml-3 download-missing-status"
            variant="contained"
            color={"warning"}
            startIcon={<DownloadTasks />}>
            {!isMobile ? "Download" : ""}
          </CustomButton>
        </Stack>
        <Box>
          <Suspense
            fallback={
              <div className="loader-center">
                <Loader color={"black"} />
              </div>
            }>
            <GlobalTableComponent
              data={
                boardCards
                  ? boardCards?.map((boardCard) => {
                      return {
                        id: boardCard?.card?.card_id,
                        is_todo: boardCard?.card?.is_todo,
                        cardId: boardCard?.card?.card_id,
                        title: boardCard?.card?.title,
                        date_created: boardCard?.card?.date_created,
                        assigned_to_user_name:
                          boardCard?.card?.assigned_to_user?.first_name || "z",
                        isSubtask:
                          boardCard?.sub_tasks?.length > 0 ? true : false,
                        ...boardCard,
                        subRow:
                          boardCard?.subRow && boardCard?.subRow?.length > 0
                            ? boardCard?.subRow?.map((cardSubTask, idx) => {
                                return {
                                  id: cardSubTask?.card_id,
                                  ...cardSubTask,
                                  key: boardCard?.key,
                                  board_name: boardCard?.board_name,
                                  board_admin: boardCard?.board_admin,
                                };
                              })
                            : null,
                      };
                    })
                  : []
              }
              headers={columns}
              subHeader={columns.map((item, index) =>
                index === 0
                  ? {
                      ...item,
                      alignContent: "center",
                    }
                  : item
              )}
              isCollapsible={true}
              isDraggableColumn={true}
              isPagination={false}
              rowCount={10}
              handleExpandedData={handleExpandedData}
              subTableProps={subTableProps}
              tableBoxClass={
                milestoneIsSelected
                  ? "parent-milestone-table-box"
                  : "parent-table-box"
              }
              tableContainerClass={"parent-table-boarder"}
              isLoading={isLoading}
              loadingProps={loader}
              loadingClass={"h-70vh"}
              headerClass={"table-header-listview"}
              handleColumOrder={handleColumOrder}
              defaultSortBy={"is_todo"}
              rowClassName={getRowClassName}
            />
          </Suspense>
        </Box>
        <CustomMenu
          id="user-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(false)}
          className="assign-board-owner-list">
          <ColumnCheckboxes
            columns={columns}
            handleSelectAll={handleSelectAll}
            handleSelectColumn={handleSelectColumn}
          />
        </CustomMenu>
      </div>
    </>
  );
}
